import React, { ChangeEvent } from 'react';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { Box, Chip, Divider, Grid, IconButton, Radio, SelectChangeEvent, Stack, TextField } from '@mui/material';
import {
  addElementIdToRecipient,
  removeElementIdFromRecipients,
  selectedElement,
  updateContentElementById,
} from 'features/document/documentSlice';
import { AzulCheckbox } from '@the-ksquare-group/azul-ds';
import Button from 'components/Button/Button';
import { DeleteRounded } from '@mui/icons-material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { isRadioButtonElement } from 'utils/typeGuards';
import { colorPalette, docGeniusPalette } from 'styles/partials/colors';
import { selectRecipients } from 'features/document/documentSlice';
import RecipientSelect from 'components/RecipientSelect/RecipientSelect';
import { Subtitle, Title } from '../../Sidebar.styled';

interface Props {
  currentElement: selectedElement;
}

const RadioButtonSidebar: React.FC<Props> = ({ currentElement }) => {
  if (!isRadioButtonElement(currentElement.data)) {
    return null;
  }

  const dispatch = useAppDispatch();

  const { elements, required, groupDirection } = currentElement.data.content;
  const {
    data: { recipient: currentRecipient },
  } = currentElement;

  const recipients = useAppSelector(selectRecipients);

  const handleOnChangeRecipient = (event: SelectChangeEvent) => {
    dispatch(removeElementIdFromRecipients(currentElement.data.id));

    const selectedRecipient = recipients.find(recipient => recipient.fullName === event.target.value);
    if (selectedRecipient) {
      dispatch(addElementIdToRecipient({ recipientId: selectedRecipient.id, elementId: currentElement.data.id }));

      dispatch(
        updateContentElementById({
          content: {},
          style: {},
          recipient: selectedRecipient,
        })
      );
    }
  };

  const handleUpdateByProperty = (property: string) => {
    return (event: ChangeEvent<HTMLInputElement>) => {
      dispatch(
        updateContentElementById({
          content: {
            [property]: event.target.checked,
          },
          style: {},
        })
      );
    };
  };

  const handleUpdateElements = (newElements: string[]) => {
    dispatch(
      updateContentElementById({
        content: {
          elements: newElements,
        },
        style: {},
      })
    );
  };

  const handleAddElement = () => {
    const newElements = [...elements, ''];
    handleUpdateElements(newElements);
  };

  const handleUpdateElementLabelByIndex = (index: number) => {
    return (event: ChangeEvent<HTMLInputElement>) => {
      const newElements = [...elements];
      newElements[index] = event.target.value;
      handleUpdateElements(newElements);
    };
  };

  const handleRemoveElementByIndex = (index: number) => {
    const newElements = [...elements];
    newElements.splice(index, 1);
    return () => {
      handleUpdateElements(newElements);
    };
  };

  const handleOnClickGroupDirection = (direction: string) => {
    dispatch(
      updateContentElementById({
        content: {
          groupDirection: direction,
        },
        style: {},
      })
    );
  };

  return (
    <Grid container direction='row' gap={2}>
      <Grid item xs={12}>
        <Title>Radio Button Block</Title>
      </Grid>
      <Box display={'flex'} flexDirection={'column'} gap={'24px'} marginTop={'26px'} width='100%'>
        <Subtitle>Recipient</Subtitle>
        <RecipientSelect
          value={currentRecipient}
          recipientOptions={recipients}
          editRecipients={false}
          onChange={handleOnChangeRecipient}
        />
      </Box>
      <Divider
        color='#C9CACB'
        flexItem
        orientation='horizontal'
        variant='middle'
        sx={{ margin: '8px 0', width: '100%' }}
      />
      <Subtitle>Group Direction</Subtitle>
      <Stack direction='row' spacing={1} marginBottom={'16px'} marginTop={'12px'}>
        <Chip
          variant={groupDirection === 'vertical' ? 'filled' : 'outlined'}
          label='Vertical'
          onClick={() => handleOnClickGroupDirection('vertical')}
        />
        <Chip
          variant={groupDirection === 'horizontal' ? 'filled' : 'outlined'}
          label='Horizontal'
          onClick={() => handleOnClickGroupDirection('horizontal')}
        />
      </Stack>
      <Grid color={colorPalette.darkGray1} item xs={12}>
        <AzulCheckbox value={required} label='Required' onChange={handleUpdateByProperty('required')} />
      </Grid>

      <Grid item xs={12}>
        <Subtitle>Radio Button Values</Subtitle>
        {elements.map((element, index) => (
          <Grid container key={`radio-button-${index}-options`} spacing={2} sx={{ paddingTop: '16px' }}>
            <Grid alignItems='center' display='flex' item xs={2}>
              <Radio
                inputProps={{ 'aria-label': `${element}-${index}` }}
                name='radio-buttons'
                sx={{ color: docGeniusPalette.primaryBlue }}
                value={index}
              />
            </Grid>

            <Grid item xs={8}>
              <TextField
                fullWidth
                name='labelInput'
                placeholder='Add Text'
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    height: '48px',
                  },
                }}
                value={element}
                onChange={handleUpdateElementLabelByIndex(index)}
              />
            </Grid>

            <Grid alignItems='center' display='flex' item xs={2} justifyContent='flex-end'>
              <IconButton
                aria-label='delete'
                color='primary'
                disabled={!(elements.length > 2)}
                onClick={handleRemoveElementByIndex(index)}
              >
                <DeleteRounded />
              </IconButton>
            </Grid>
          </Grid>
        ))}
      </Grid>

      <Button variant='secondary' startIcon={<AddRoundedIcon />} onClick={handleAddElement}>
        Add Radio Button
      </Button>
    </Grid>
  );
};

export default RadioButtonSidebar;
